// 選んだファイル・フォルダの ZIP ダウンロード (見積もり・作成・作成済み ZIP の一覧)
// tasks/plan_bulk_download_zip.md (D1・D2・D14)・tasks/bulk_download_zip/impl_spec_frontend.md (B1)
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import _ from 'lodash';
import { FOLDER_ARCHIVE_CREATED_EVENT } from './useFolderArchive';

const STATUS_PENDING = 'PENDING';
const STATUS_RUNNING = 'RUNNING';

// 準備中ポーリングの間隔(10秒)と上限回数(30回=5分)。useFolderArchive.js と同じ値
const POLL_INTERVAL_MS = 10000;
const POLL_MAX_COUNT = 30;
// 見積もりは、最後の変更からこの時間待って 1 回だけ送る
const ESTIMATE_DEBOUNCE_MS = 500;

const ARCHIVES_URL = '/api/folders/archives';
const ESTIMATE_URL = '/api/folders/archives/selection-estimate';
const STORE_URL = '/api/folders/archives/selection';
const MODAL_SELECTOR = '#selectionArchiveModal';

const translate = (key, fallback = '') => _.get(window.i18n, key) || fallback || key;

// ':name' 形式のプレースホルダを置き換える (Blade が出した lang の文言に値を入れるのに使う)
const fmt = (template, params = {}) =>
  Object.keys(params).reduce(
    (text, name) => text.split(`:${name}`).join(String(params[name])),
    String(template ?? '')
  );

const isPreparingStatus = (status) => status === STATUS_PENDING || status === STATUS_RUNNING;

export const useSelectionArchive = ({
  videoIds,
  folderIds,
  itemsList,
  count,
  removeFromBasket,
  clearBasket,
}) => {
  const archives = ref([]);
  const downloadableCount = ref(0);
  const estimate = ref(null);
  const estimateLoading = ref(false);
  const includeSubfolders = ref(false);
  const creating = ref(false);
  const createdNotice = ref('');
  const errorMessage = ref('');
  const pollTimeoutReached = ref(false);

  let modalOpen = false;
  let pollTimerId = null;
  let pollCount = 0;
  let estimateTimerId = null;
  let estimateSeq = 0;

  const hasFolders = computed(() => (folderIds.value || []).length >= 1);
  const hasPreparing = computed(() => archives.value.some((a) => isPreparingStatus(a.status)));

  // 選択が 0 件・見積もり中・見積もりがない・message があるとき・作成中は押せない
  const canCreate = computed(() =>
    count.value >= 1
    && !estimateLoading.value
    && estimate.value !== null
    && !estimate.value.message
    && !creating.value
  );

  const resolveApiErrorMessage = (error, fallbackKey, fallbackText) => {
    const responseMessage = error?.response?.data?.message;
    if (typeof responseMessage === 'string' && responseMessage !== '') {
      return responseMessage;
    }
    return translate(fallbackKey, fallbackText);
  };

  const buildPayload = () => ({
    video_ids: [...(videoIds.value || [])],
    folder_ids: [...(folderIds.value || [])],
    include_subfolders: hasFolders.value && includeSubfolders.value,
  });

  // ---- 一覧 (D14) ----
  const fetchArchives = async () => {
    try {
      const response = await window.axios.get(ARCHIVES_URL);
      archives.value = Array.isArray(response.data?.archives) ? response.data.archives : [];
      downloadableCount.value = Number(response.data?.downloadable_count || 0);
    } catch (error) {
      // 通信失敗時は直前の状態のまま続ける (useFolderArchive.js と同じ)
    }
  };

  const clearPollTimer = () => {
    if (pollTimerId !== null) {
      clearInterval(pollTimerId);
      pollTimerId = null;
    }
    pollCount = 0;
  };

  // 準備中があるあいだだけ 10 秒ごとに取り直す。30 回で止めて pollTimeoutReached を立てる。
  const startPolling = () => {
    clearPollTimer();
    pollTimeoutReached.value = false;
    if (!hasPreparing.value) {
      return;
    }
    pollTimerId = setInterval(async () => {
      pollCount += 1;
      await fetchArchives();
      if (!hasPreparing.value) {
        clearPollTimer();
        return;
      }
      if (pollCount >= POLL_MAX_COUNT) {
        clearPollTimer();
        pollTimeoutReached.value = true;
      }
    }, POLL_INTERVAL_MS);
  };

  // 1 回取り直し、準備中があれば取り直しを 0 回目から始め直す
  const refreshArchives = async () => {
    await fetchArchives();
    startPolling();
  };

  // ---- 見積もり (D2) ----
  const cancelEstimateTimer = () => {
    if (estimateTimerId !== null) {
      clearTimeout(estimateTimerId);
      estimateTimerId = null;
    }
  };

  const sendEstimate = async (seq) => {
    try {
      const response = await window.axios.post(ESTIMATE_URL, buildPayload());
      if (seq !== estimateSeq) {
        return; // 送っている途中に次の変更が来た。古い返りは捨てる
      }
      estimate.value = response.data;
      errorMessage.value = ''; // 前の失敗の表示は、次の問い合わせが成功したら消す
    } catch (error) {
      if (seq !== estimateSeq) {
        return;
      }
      estimate.value = null;
      errorMessage.value = resolveApiErrorMessage(
        error,
        'folder.selection_archive_estimate_failed',
        'ZIPに入るファイルを確認できませんでした。時間をおいて再度お試しください。'
      );
    }
    if (seq === estimateSeq) {
      estimateLoading.value = false;
    }
  };

  const scheduleEstimate = ({ immediate = false } = {}) => {
    cancelEstimateTimer();
    estimateSeq += 1;
    const seq = estimateSeq;
    estimate.value = null;
    if (count.value < 1) {
      estimateLoading.value = false;
      return;
    }
    estimateLoading.value = true;
    if (immediate) {
      sendEstimate(seq);
      return;
    }
    estimateTimerId = setTimeout(() => {
      estimateTimerId = null;
      sendEstimate(seq);
    }, ESTIMATE_DEBOUNCE_MS);
  };

  // ポップアップを開いている間だけ、選択・「子フォルダを含む」の変更で見積もりを出し直す
  watch(
    () => `${(videoIds.value || []).join(',')}|${(folderIds.value || []).join(',')}|${hasFolders.value && includeSubfolders.value}`,
    () => {
      if (modalOpen) {
        scheduleEstimate();
      }
    }
  );

  // ---- ポップアップ ----
  const onModalHidden = () => {
    modalOpen = false;
    cancelEstimateTimer();
    estimateSeq += 1; // 返ってくる途中の見積もりは捨てる
    estimateLoading.value = false;
    // 開くたびにオフから始める (計画 D2)。開くときに戻すと watch が modalOpen=true で 2 回目の見積もりを送るため、閉じたときに戻す
    includeSubfolders.value = false;
  };

  const showSelectionArchiveModal = () => {
    modalOpen = true;
    createdNotice.value = '';
    errorMessage.value = '';
    if (window.jQuery) {
      window.jQuery(MODAL_SELECTOR).modal('show');
    }
    scheduleEstimate({ immediate: true });
    refreshArchives();
  };

  // ---- 作成 ----
  const createArchive = async () => {
    if (!canCreate.value) {
      return;
    }
    creating.value = true;
    errorMessage.value = '';
    createdNotice.value = '';
    try {
      await window.axios.post(STORE_URL, buildPayload());
      createdNotice.value = translate(
        'folder.download_started_notice',
        'ZIPの作成を開始しました。完了時にメールでお知らせします。'
      );
      clearBasket();
      await refreshArchives();
    } catch (error) {
      errorMessage.value = resolveApiErrorMessage(
        error,
        'folder.selection_archive_failed',
        'ZIPの作成を開始できませんでした。時間をおいて再度お試しください。'
      );
      // submitJob 失敗で FAILED の ZIP が記録されたときは、一覧にも出す
      if (error?.response?.status === 500) {
        await refreshArchives();
      }
    } finally {
      creating.value = false;
    }
  };

  const removeItem = (item) => removeFromBasket(item.id, item.type);

  // フォルダのパネルで作った ZIP も一覧とバッジに入れるため、取り直しを 0 回目から始め直す
  const onFolderArchiveCreated = () => {
    refreshArchives();
  };
  let folderArchiveListenerAdded = false;

  // ZIP のメールのリンク (?open_archives=1) で来たとき、アドレス欄からそれだけを消して true を返す
  const consumeOpenArchivesFromUrl = () => {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('open_archives')) {
        return false;
      }
      url.searchParams.delete('open_archives');
      window.history.replaceState({}, '', url.toString());
      return true;
    } catch (error) {
      return false;
    }
  };

  onMounted(() => {
    const openRequested = consumeOpenArchivesFromUrl();
    // ポップアップは ZIP 作成ボタンと同じ条件でだけ描画される。無い画面 (ログイン画面など) では何も取らない
    if (!document.querySelector(MODAL_SELECTOR)) {
      return;
    }
    // バッジの件数のため、ダッシュボードを開いたときに 1 回取る
    refreshArchives();
    window.addEventListener(FOLDER_ARCHIVE_CREATED_EVENT, onFolderArchiveCreated);
    folderArchiveListenerAdded = true;
    if (window.jQuery) {
      window.jQuery(MODAL_SELECTOR).on('hidden.bs.modal', onModalHidden);
    }
    if (openRequested) {
      showSelectionArchiveModal();
    }
  });

  onBeforeUnmount(() => {
    if (folderArchiveListenerAdded) {
      window.removeEventListener(FOLDER_ARCHIVE_CREATED_EVENT, onFolderArchiveCreated);
      folderArchiveListenerAdded = false;
    }
    clearPollTimer();
    cancelEstimateTimer();
    if (window.jQuery) {
      window.jQuery(MODAL_SELECTOR).off('hidden.bs.modal', onModalHidden);
    }
  });

  return {
    selectionArchives: archives,
    selectionArchiveDownloadableCount: downloadableCount,
    selectionArchiveEstimate: estimate,
    selectionArchiveEstimateLoading: estimateLoading,
    selectionArchiveIncludeSubfolders: includeSubfolders,
    selectionArchiveCreating: creating,
    selectionArchiveCreatedNotice: createdNotice,
    selectionArchiveErrorMessage: errorMessage,
    selectionArchivePollTimeoutReached: pollTimeoutReached,
    selectionArchiveHasFolders: hasFolders,
    selectionArchiveCanCreate: canCreate,
    selectionArchiveItems: itemsList,
    showSelectionArchiveModal,
    createSelectionArchive: createArchive,
    removeSelectionArchiveItem: removeItem,
    refreshSelectionArchives: refreshArchives,
    formatArchiveText: fmt,
  };
};
